import { Injectable, NgZone } from '@angular/core';
import { Platform } from 'ionic-angular';
import { APP_CONFIG } from '../../environment';
import { Env } from '../../environment/env';
import { ColorProvider } from '../color/color';
import { PaymentMethodType } from '../../model/types/payment-method-type';
import { IPspCardData } from '../../model/types/psp-processor-type';
import { HttpProvider } from '../http/http';
import { LoggerEvent, LoggerProvider } from '../logger/logger';
import { IWorldpay3dsChallengeOptions, IWorldpay3dsChallengeResult, Worldpay3dsPlugin } from './worldpay-3ds-sdk';

declare var Worldpay: any;

// Worldpay 3DS pspAction as returned by the BE on the DeviceDataCollection (jwt/url/bin) and Challenge
// (reference/url/jwt/payload) order responses. 
export interface IWorldpayThreeDsAction {
  jwt?: string;
  url?: string;
  bin?: string;
  reference?: string;
  payload?: string;
}



// The SDK caps every style value at 50 characters and drops the ENTIRE config on one violation
// (console error 'invalidStylePropertyLength'), so keep values - especially font stacks - short.
// Resolved at init time so the active brand theme (--psp-input-text) drives the iframe styles.
const worldpayFieldStyles = () => {
  const textColor = ColorProvider.getColorValue('--psp-input-text') || '#313F5B';
  return {
    'input': { 'color': textColor, 'font-size': '16px', 'font-family': '-apple-system, Roboto, sans-serif' },
    'input.is-onfocus': { 'color': textColor },
    'input::placeholder': { 'color': textColor, 'font-size': '16px', 'font-family': '-apple-system, Roboto, sans-serif' }
  };
};


const WORLDPAY_FIELD_ERROR_MSG: { [field: string]: string } = {
  pan: 'form.createCardAccountForm.cardNumber.cardGeneralRegex', 
  expiry: 'form.createCardAccountForm.expirationDateInvalid',
  cvv: 'form.createCardAccountForm.cvv.incomplete'
};


export interface IWorldpayFieldStates {
  'is-valid'?: boolean;
  'is-invalid'?: boolean;
  'is-empty'?: boolean;
  'is-onfocus'?: boolean;
}

export interface IWorldpayStateMessage {
  event: string;
  response: {
    field: 'pan' | 'expiry' | 'cvv' | 'cvvOnly';
    states: IWorldpayFieldStates;
  };
}


@Injectable()
export class WorldpayProvider {
  // Per-field UI state in the shape rmt-worldpay-card-fields binds ({pan|expiry|cvv: {valid, empty,
  // focus, error}}; error is a translation key). 
  fieldState: any;
  // True when every mounted field is valid (cvv only in cvc mode); hosts derive their submit gating from this.
  allFieldsValid = false;
  cardBrand: any = null;
  brandIcon: any = null;
  // 'debit' | 'credit' from the SDK's `wp:funding-type:change` hook (BIN lookup after 12 PAN digits)
  fundingType: string | null = null;

  private checkout: any = null;
  private messageListener: any = null;
  private brandListener: any = null;
  private fundingTypeListener: any = null;
  private formEl: any = null;
  private cvcOnly = false;
  
  private initGeneration = 0;

  constructor(private zone: NgZone, private httpProvider: HttpProvider, private worldpay3ds: Worldpay3dsPlugin, private platform: Platform) { }


  mountCardFields(opts: {
    checkoutId: string;
    containerId: string;
    // Worldpay SDK brand names (e.g. ['visa', 'mastercard']); a PAN of any other brand reports is-invalid.
    acceptedCardBrands?: string[];
    onFieldState?: (field: string, states: IWorldpayFieldStates) => void;
    onReady?: (error: any) => void;
  }) {
    this.resetFieldState(false);
    this.listenValidation(this.trackFieldState(opts.onFieldState));
    const extra: any = { enablePanFormatting: true };
    if (opts.acceptedCardBrands && opts.acceptedCardBrands.length)
      extra.acceptedCardBrands = opts.acceptedCardBrands;
    this.initCheckout({
      label: 'checkout',
      checkoutId: opts.checkoutId,
      containerId: opts.containerId,
      fields: {
        pan: { selector: '#' + opts.containerId + '_wp_pan', placeholder: '' },
        expiry: { selector: '#' + opts.containerId + '_wp_expiry', placeholder: '' },
        cvv: { selector: '#' + opts.containerId + '_wp_cvv', placeholder: '' }
      },
      extra,
      brand: { formId: opts.containerId + '_wp_form', onBrand: (brand) => this.trackBrand(brand) },
      onReady: opts.onReady
    });
  }


  mountCvcField(opts: {
    checkoutId: string;
    containerId: string;
    onFieldState?: (field: string, states: IWorldpayFieldStates) => void;
    onReady?: (error: any) => void;
  }) {
    this.resetFieldState(true);
    this.listenValidation(this.trackFieldState(opts.onFieldState));
    this.initCheckout({
      label: 'CVC checkout',
      checkoutId: opts.checkoutId,
      containerId: opts.containerId,
      fields: {
        cvvOnly: { selector: '#' + opts.containerId + '_wp_cvvOnly', placeholder: '' }
      },
      onReady: opts.onReady
    });
  }

 
  async generateSession(): Promise<string> {
    if (!this.checkout)
      return Promise.reject(new Error('Worldpay checkout not initialized'));
    return new Promise<string>((resolve, reject) => {
      this.checkout.generateSessionState((error: any, sessions: any) => {
        if (error || !sessions) {
          reject(error || new Error('Worldpay generateSessionState returned empty'));
          return;
        }
        resolve(sessions);
      });
    });
  }

  // Tokenizes the card session via BE card/add and returns the raw response ({accountId, ...}).
  async addCard(opts: {
    sessionUrl: string;
    paymentMethod?: PaymentMethodType | null;
    doNotReuse: boolean;
    billingAddress: any;
    isSendMoneyFlow?: boolean;
  }): Promise<any> {
    const cardData: IPspCardData = { CardSessionUrl: opts.sessionUrl };
    if (this.cardBrand)
      cardData.CardBrand = this.cardBrand;
    const payload: any = {
      PaymentMethod: opts.paymentMethod,
      DoNotReuse: opts.doNotReuse,
      PspPaymentSessionData: {
        cardData,
        additionalDetails: false,
        billingAddress: opts.billingAddress
      }
    };
    if (opts.isSendMoneyFlow)
      payload.isSendMoneyFlow = true;
    return this.httpProvider.post(`${APP_CONFIG.remittanceApiBaseUrl}account/card/add`, payload);
  }

  isNativeChannel(): boolean {
    return this.platform.is('cordova');
  }

  async runDeviceDataCollection(action: IWorldpayThreeDsAction): Promise<string | null> {
    if (this.isNativeChannel())
      return this.runNativeDeviceDataCollection(action.jwt);
    return new Promise<string | null>((resolve) => {
      
      let expectedOrigin;
      try {
        expectedOrigin = new URL(action.url).origin;
      } catch (e) {
        LoggerProvider.error(this, LoggerEvent.Payment, 'Worldpay DDC action URL invalid', action.url);
        resolve(null);
        return;
      }
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      const form = document.createElement('form');
      form.style.display = 'none';
      form.method = 'POST';
      form.action = action.url;
      // Cardinal expects the uppercase field names JWT / Bin.
      form.appendChild(this.hiddenInput('JWT', action.jwt));
      form.appendChild(this.hiddenInput('Bin', action.bin));

      let timeout = null;
      let listener = null;
      const finish = (sessionId: string | null) => {
        if (timeout)
          window.clearTimeout(timeout);
        if (listener)
          window.removeEventListener('message', listener);
        iframe.remove();
        form.remove();
        this.zone.run(() => resolve(sessionId));
      };

      listener = (event: any) => {
        // Only accept the profile.completed message coming back from Cardinal itself.
        if (event.origin !== expectedOrigin)
          return;
        let data = event.data;
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch (e) {
            return;
          }
        }
        if (!data || data.MessageType !== 'profile.completed')
          return;
        if (data.Status && data.SessionId) {
          LoggerProvider.info(this, 'Worldpay DDC completed');
          finish(data.SessionId);
        } else {
          LoggerProvider.warning(this, LoggerEvent.Payment, 'Worldpay DDC completed without session', data);
          finish(null);
        }
      };
      window.addEventListener('message', listener);
      // Worldpay documents a 10 second maximum response time for the DDC call.
      timeout = window.setTimeout(() => {
        LoggerProvider.warning(this, LoggerEvent.Payment, 'Worldpay DDC timed out');
        finish(null);
      }, 10000);

      const frameName = 'wp_ddc_iframe';
      iframe.name = frameName;
      form.target = frameName;
      document.body.appendChild(iframe);
      document.body.appendChild(form);
      form.submit();
    });
  }

  private async runNativeDeviceDataCollection(jwt: string): Promise<string | null> {
    try {
      // Reset the SDK singleton so every attempt initializes fresh.
      await this.worldpay3ds.cleanup().catch(() => null);
      const consumerSessionId = await this.worldpay3ds.setup({ jwt, environment: this.cardinalEnvironment() });
      LoggerProvider.info(this, 'Worldpay native DDC completed');
      return consumerSessionId || null;
    } catch (e) {
      LoggerProvider.warning(this, LoggerEvent.Payment, 'Worldpay native DDC failed', e);
      return null;
    }
  }

  async runNativeChallenge(opts: IWorldpay3dsChallengeOptions): Promise<IWorldpay3dsChallengeResult> {
    try {
      return await this.worldpay3ds.continueChallenge(opts);
    } finally {
      await this.worldpay3ds.cleanup().catch(() => null);
    }
  }

  private cardinalEnvironment() {
    return APP_CONFIG.env === Env.PROD ? 'production' : 'staging';
  }

  private hiddenInput(name: string, value: string) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    return input;
  }

  // Tears down the checkout and both listeners (call on leave / before re-mount).
  remove() {
    this.initGeneration++;
    this.removeCheckout();
    this.removeListeners();
  }

  private resetFieldState(cvcOnly: boolean) {
    this.cvcOnly = cvcOnly;
    this.allFieldsValid = false;
    this.fieldState = {
      pan: { valid: false, empty: true, focus: false, error: '' },
      expiry: { valid: false, empty: true, focus: false, error: '' },
      cvv: { valid: false, empty: true, focus: false, error: '' }
    };
    this.cardBrand = null;
    this.brandIcon = null;
    this.fundingType = null;
  }

  fundingPaymentMethod(): PaymentMethodType | null {
    if (this.fundingType === 'debit')
      return PaymentMethodType.DebitCard;
    else if (this.fundingType === 'credit')
      return PaymentMethodType.CreditCard;
    return null;
  }

  // Wraps the host's optional onFieldState callback so the provider's fieldState/allFieldsValid are
  // always maintained first.
  private trackFieldState(onFieldState?: (field: string, states: IWorldpayFieldStates) => void) {
    return (field: string, states: IWorldpayFieldStates) => {
      const fieldState = this.fieldState[field];
      if (!fieldState)
        return;
      fieldState.valid = !!states['is-valid'];
      fieldState.empty = !!states['is-empty']; // drives the in-field icon / brand badges (show when empty)
      fieldState.focus = !!states['is-onfocus']; // drives the focus ring
      fieldState.error = this.getFieldError(field, states);
      this.allFieldsValid = this.cvcOnly
        ? this.fieldState.cvv.valid
        : this.fieldState.pan.valid && this.fieldState.expiry.valid && this.fieldState.cvv.valid;
      if (onFieldState)
        onFieldState(field, states);
    };
  }

  // Errors show only once a non-empty field is invalid on blur, to avoid nagging mid-typing.
  private getFieldError(field: string, states: IWorldpayFieldStates): string {
    if (states['is-empty'] || states['is-valid'] || states['is-onfocus'])
      return '';
    if (!states['is-invalid'])
      return '';
    return WORLDPAY_FIELD_ERROR_MSG[field] || '';
  }

  private trackBrand(brand: string | null) {
    this.cardBrand = brand;
    this.brandIcon = this.getBrandIcon(brand);
  }

  private getBrandIcon(cardBrand: string | null) {
    const brand = (cardBrand || '').toLowerCase();
    if (brand === 'visa')
      return 'assets/icons/card_visa.svg';
    if (brand === 'mastercard' || brand === 'master')
      return 'assets/img/payment-card/mastercard.svg';
    return null;
  }

  // Worldpay injects its hosted card iframes into existing DOM containers, so wait a frame to ensure the
  // field containers (bound to containerId) have rendered before init.
  private initCheckout(cfg: {
    label: string;
    checkoutId: string;
    containerId: string;
    fields: any;
    extra?: any;
    brand?: { formId: string; onBrand: (brand: string | null) => void };
    onReady?: (error: any) => void;
  }) {
    const generation = ++this.initGeneration;
    window.requestAnimationFrame(() => {
      if (generation !== this.initGeneration)
        return; // superseded by a newer mount (or remove) before it ran
      this.removeCheckout();
      if (cfg.brand)
        this.listenCardBrands(cfg.brand.formId, cfg.brand.onBrand);
      LoggerProvider.info(this, 'Initializing Worldpay ' + cfg.label + ' for container ID ' + cfg.containerId);
      Worldpay.checkout.init({
        id: cfg.checkoutId,
        form: '#' + cfg.containerId + '_wp_form',
        fields: cfg.fields,
        styles: worldpayFieldStyles(),
        ...(cfg.extra || {})
      }, (error: any, checkout: any) => {
        if (error) {
          LoggerProvider.warning(this, LoggerEvent.PageException, 'Worldpay ' + cfg.label + ' init failed', error);
          if (cfg.onReady)
            cfg.onReady(error);
          return;
        }
        if (generation !== this.initGeneration) {
          checkout.remove(); // superseded while the SDK init was in flight - drop this instance's iframes
          return;
        }
        LoggerProvider.info(this, 'Worldpay ' + cfg.label + ' initialized');
        this.checkout = checkout;
        if (cfg.onReady)
          cfg.onReady(null);
      });
    });
  }

  private listenValidation(onFieldState: (field: string, states: IWorldpayFieldStates) => void) {
    this.removeListeners();


    // Brand detection is handled separately by the documented `wp:card-brands:change` hook.
    this.messageListener = (event: any) => {
      let data = event && event.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (e) {
          return;
        }
      }
      const message: IWorldpayStateMessage = data;
      if (!message || !message.response)
        return;
      if (message.event !== 'state' || !message.response.states)
        return;
      const field = message.response.field;
      if (field !== 'pan' && field !== 'expiry' && field !== 'cvv' && field !== 'cvvOnly')
        return;
      this.zone.run(() => {

        onFieldState(field === 'cvvOnly' ? 'cvv' : field, message.response.states);
      });
    };
    window.addEventListener('message', this.messageListener);
  }


  private listenCardBrands(formId: string, onBrand: (brand: string | null) => void) {
    this.removeBrandListener();
    this.formEl = document.getElementById(formId);
    if (!this.formEl)
      return;
    this.brandListener = (event: any) => {
      const brands = event && event.detail && event.detail.brands;
      this.zone.run(() => {
        onBrand(brands && brands.length ? brands[0] : null);
      });
    };
    this.fundingTypeListener = (event: any) => {
      const fundingType = event && event.detail && event.detail['funding-type'];
      this.zone.run(() => {
        this.fundingType = fundingType || null;
      });
    };
    this.formEl.addEventListener('wp:card-brands:change', this.brandListener);
    this.formEl.addEventListener('wp:funding-type:change', this.fundingTypeListener);
  }

  private removeListeners() {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = null;
    }
    this.removeBrandListener();
  }

  private removeBrandListener() {
    if (this.formEl && this.brandListener)
      this.formEl.removeEventListener('wp:card-brands:change', this.brandListener);
    if (this.formEl && this.fundingTypeListener)
      this.formEl.removeEventListener('wp:funding-type:change', this.fundingTypeListener);
    this.brandListener = null;
    this.fundingTypeListener = null;
    this.formEl = null;
  }

  private removeCheckout() {
    if (this.checkout) {
      this.checkout.remove();
      this.checkout = null;
    }
  }
}



// WEBPACK FOOTER //
// ./src/providers/worldpay/worldpay.ts